import React, { useState, useEffect, useCallback } from 'react';
import { validationsAPI, arretsMaladieAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Timer, ReceiptText, CalendarX, Activity, FolderOpen, ChevronRight,
  CheckCircle2, XCircle, ArrowLeft, Clock, RefreshCw, Bell,
  AlertCircle, Filter, ExternalLink, FileText, Eye, X, Download,
  ChevronDown, ChevronUp, Search, Calendar, User,
} from 'lucide-react';
import {
  exportNoteFraisPDF, exportNoteFraisCSV,
  exportArretMaladiePDF, exportArretMaladieCSV,
  exportAbsencePDF, exportAbsenceCSV,
} from '../utils/exportPDF';

// ── Config catégories ─────────────────────────────────────────
const CATEGORIES = [
  { key: 'timesheets',    label: 'Feuilles de temps', icon: Timer,       color: '#3B82F6' },
  { key: 'notes_frais',  label: 'Notes de frais',    icon: ReceiptText, color: '#EA580C' },
  { key: 'absences',     label: 'Congés',            icon: CalendarX,   color: '#7C3AED' },
  { key: 'arrets_maladie', label: 'Arrêts maladie',  icon: Activity,    color: '#DC2626' },
  { key: 'documents',    label: 'Documents',         icon: FolderOpen,  color: '#0891B2' },
];

const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

// ── Modal commentaire ─────────────────────────────────────────
function CommentModal({ title, action, onConfirm, onClose, requireComment }) {
  const [commentaire, setCommentaire] = useState('');
  const canSubmit = !requireComment || commentaire.trim().length > 0;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 14, width: '100%', maxWidth: 420,
                    border: '1px solid var(--border-strong)', padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: 12 }}>{title}</div>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                         textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
          Commentaire {requireComment && <span style={{ color: '#DC2626' }}>*</span>}
        </label>
        <textarea value={commentaire} onChange={e => setCommentaire(e.target.value)}
          rows={3} placeholder={requireComment ? 'Motif obligatoire…' : 'Commentaire optionnel…'}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
                   border: '1.5px solid var(--border)', background: 'var(--bg-elevated)',
                   color: 'var(--text-primary)', fontSize: '0.875rem', resize: 'vertical', outline: 'none' }} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose}
            style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid var(--border)',
                     background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 500, fontSize: '0.875rem' }}>
            Annuler
          </button>
          <button onClick={() => onConfirm(commentaire)} disabled={!canSubmit}
            style={{ padding: '9px 20px', borderRadius: 9, border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed',
                     background: action === 'VALIDE' ? '#059669' : '#DC2626',
                     color: '#fff', fontWeight: 700, fontSize: '0.875rem', opacity: canSubmit ? 1 : 0.5 }}>
            {action === 'VALIDE' ? '✓ Valider' : '✗ Rejeter'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Drawer détail ──────────────────────────────────────────────
function DetailDrawer({ item, catKey, onClose, onAction }) {
  const cat = CATEGORIES.find(c => c.key === catKey);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!item) return;
    setDetail(null);
    if (catKey === 'arrets_maladie') {
      setLoading(true);
      validationsAPI.getArretDetail(item.id)
        .then(r => setDetail(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else if (catKey === 'notes_frais') {
      setLoading(true);
      validationsAPI.getNoteDetail(item.id)
        .then(r => setDetail(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setDetail(item);
    }
  }, [item, catKey]);

  if (!item) return null;

  const isPending = (item.statut || 'SOUMIS') === 'SOUMIS';

  // ── Composants visuels ─────────────────────────────────────
  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
      {title && (
        <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
          {title}
        </div>
      )}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 10,
                    border: '1px solid var(--border)', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );

  const Row = ({ label, value, highlight, last }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  padding: '10px 14px', borderBottom: last ? 'none' : '1px solid var(--border)',
                  gap: 12 }}>
      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', flexShrink: 0, lineHeight: 1.5 }}>
        {label}
      </span>
      <span style={{ fontSize: '0.875rem', fontWeight: highlight ? 700 : 500,
                     color: highlight ? cat.color : 'var(--text-primary)',
                     textAlign: 'right', lineHeight: 1.5, wordBreak: 'break-word' }}>
        {value || '—'}
      </span>
    </div>
  );

  const FileRow = ({ name, url, last }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <FileText size={14} style={{ color: '#DC2626', flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--text-primary)',
                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </span>
      {url && (
        <a href={url} target="_blank" rel="noreferrer"
           style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                    borderRadius: 6, background: '#0891B210', color: '#0891B2',
                    fontSize: '0.75rem', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
          <Eye size={12} /> Voir
        </a>
      )}
    </div>
  );

  const renderContent = () => {
    if (loading && !detail) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160,
                      color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Chargement…
        </div>
      );
    }
    const d = detail || item;

    if (catKey === 'timesheets') {
      return (
        <>
          <Section title="Informations">
            <Row label="Collaborateur" value={`${d.user_prenom || ''} ${d.user_nom || ''}`} />
            <Row label="Période" value={`${MONTHS_FR[(d.mois||1)-1]} ${d.annee}`} highlight />
            {d.commentaire_rh && <Row label="Commentaire RH" value={d.commentaire_rh} last />}
          </Section>
          <button onClick={() => { onClose(); navigate(`/timesheets/${d.annee}/${d.mois}`); }}
            style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                     padding: '11px 16px', borderRadius: 9, border: '1.5px solid var(--border)',
                     background: 'var(--bg-elevated)', cursor: 'pointer',
                     color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.85rem', justifyContent: 'center' }}>
            <ExternalLink size={14} /> Ouvrir la feuille de temps complète
          </button>
        </>
      );
    }

    if (catKey === 'notes_frais') {
      return (
        <>
          <Section title="Demandeur">
            <Row label="Collaborateur" value={`${d.user_prenom || ''} ${d.user_nom || ''}`} last />
          </Section>
          <Section title="Détails">
            <Row label="Désignation" value={d.designation} />
            <Row label="Montant TTC" value={d.montant_ttc != null ? `${parseFloat(d.montant_ttc).toFixed(2)} €` : '—'} highlight />
            <Row label="Poste" value={(d.poste || '').replace(/_/g, ' ')} />
            <Row label="Date d'effet" value={fmtDate(d.date_effet)} last />
          </Section>
          {d.commentaire && (
            <Section title="Note du collaborateur">
              <div style={{ padding: '10px 14px', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {d.commentaire}
              </div>
            </Section>
          )}
          {d.justificatif_name && (
            <Section title="Justificatif">
              <FileRow name={d.justificatif_name} url={d.justificatif_url} last />
            </Section>
          )}
        </>
      );
    }

    if (catKey === 'absences') {
      return (
        <>
          <Section title="Demandeur">
            <Row label="Collaborateur" value={`${d.user_prenom || ''} ${d.user_nom || ''}`} last />
          </Section>
          <Section title="Détails">
            <Row label="Type" value={(d.type_conge || '').replace(/_/g, ' ')} highlight />
            <Row label="Du" value={fmtDate(d.date_debut)} />
            <Row label="Au" value={fmtDate(d.date_fin)} />
            <Row label="Durée" value={d.nb_jours != null ? `${d.nb_jours} jour${d.nb_jours > 1 ? 's' : ''}` : '—'} last />
          </Section>
          {d.description && (
            <Section title="Motif">
              <div style={{ padding: '10px 14px', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {d.description}
              </div>
            </Section>
          )}
          {d.commentaire_rh && (
            <Section title="Commentaire RH">
              <div style={{ padding: '10px 14px', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {d.commentaire_rh}
              </div>
            </Section>
          )}
        </>
      );
    }

    if (catKey === 'arrets_maladie') {
      return (
        <>
          <Section title="Demandeur">
            <Row label="Collaborateur" value={`${d.user_prenom || ''} ${d.user_nom || ''}`} last />
          </Section>
          <Section title="Détails">
            <Row label="Du" value={fmtDate(d.date_debut)} />
            <Row label="Au" value={fmtDate(d.date_fin)} />
            <Row label="Durée" value={d.nb_jours != null ? `${d.nb_jours} jour${d.nb_jours > 1 ? 's' : ''}` : '—'} highlight />
            {d.depart_midi && <Row label="" value="Départ à midi le 1er jour (−0.5j)" />}
            {d.retour_midi && <Row label="" value="Retour à 14h le dernier jour (−0.5j)" last />}
            {!d.depart_midi && !d.retour_midi && <Row label="" value="" last />}
          </Section>
          {d.description && (
            <Section title="Description">
              <div style={{ padding: '10px 14px', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {d.description}
              </div>
            </Section>
          )}
          {d.justificatifs && d.justificatifs.length > 0 && (
            <Section title={`Justificatifs (${d.justificatifs.length})`}>
              {d.justificatifs.map((j, i) => (
                <FileRow key={j.id} name={j.file_name} url={j.file_url}
                         last={i === d.justificatifs.length - 1} />
              ))}
            </Section>
          )}
          {d.commentaire_rh && (
            <Section title="Commentaire RH">
              <div style={{ padding: '10px 14px', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {d.commentaire_rh}
              </div>
            </Section>
          )}
        </>
      );
    }

    if (catKey === 'documents') {
      return (
        <>
          <Section title="Demandeur">
            <Row label="Collaborateur" value={`${d.user_prenom || ''} ${d.user_nom || ''}`} last />
          </Section>
          <Section title="Détails">
            <Row label="Type" value={(d.document_type || '').replace(/_/g, ' ')} />
            <Row label="Titre" value={d.title} highlight last />
          </Section>
          {d.commentaire && (
            <Section title="Note">
              <div style={{ padding: '10px 14px', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                {d.commentaire}
              </div>
            </Section>
          )}
          {d.file_name && (
            <Section title="Fichier">
              <FileRow name={d.file_name} url={d.file_url} last />
            </Section>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <div onClick={onClose}
           style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9000 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 480,
        background: 'var(--bg-card)', boxShadow: '-6px 0 32px rgba(0,0,0,0.22)',
        zIndex: 9001, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header drawer */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
                      background: `linear-gradient(135deg, ${cat.color}08 0%, transparent 100%)` }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${cat.color}18`,
                        border: `1.5px solid ${cat.color}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <cat.icon size={20} color={cat.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)', lineHeight: 1.2 }}>
              {cat.label}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {isPending ? 'En attente de validation' : 'Déjà traité'}
            </div>
          </div>
          <button onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                     background: 'var(--bg-elevated)', cursor: 'pointer', color: 'var(--text-muted)',
                     display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X size={14} />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {renderContent()}
        </div>

        {/* Boutons export (items traités) */}
        {!isPending && (() => {
          const d = detail || item;
          const exportFns = {
            notes_frais:    [() => exportNoteFraisPDF(d),    () => exportNoteFraisCSV(d)],
            arrets_maladie: [() => exportArretMaladiePDF(d), () => exportArretMaladieCSV(d)],
            absences:       [() => exportAbsencePDF(d),      () => exportAbsenceCSV(d)],
          };
          const fns = exportFns[catKey];
          if (!fns) return null;
          return (
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)',
                          display: 'flex', gap: 8, flexShrink: 0, background: 'var(--bg-elevated)' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', alignSelf: 'center', marginRight: 4 }}>
                Télécharger :
              </span>
              <button onClick={fns[0]}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                         borderRadius: 8, border: '1px solid rgba(5,150,105,0.3)',
                         background: 'rgba(5,150,105,0.07)', color: '#059669',
                         cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                <Download size={12} /> PDF
              </button>
              <button onClick={fns[1]}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                         borderRadius: 8, border: '1px solid var(--border)',
                         background: 'var(--bg-card)', color: 'var(--text-secondary)',
                         cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                <FileText size={12} /> Excel
              </button>
            </div>
          );
        })()}

        {/* Boutons d'action (items en attente) */}
        {isPending && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)',
                        display: 'flex', gap: 10, flexShrink: 0, background: 'var(--bg-elevated)' }}>
            <button onClick={() => onAction(item, 'REJETE')}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                       padding: '11px', borderRadius: 10, border: '1.5px solid rgba(220,38,38,0.4)',
                       background: 'rgba(220,38,38,0.07)', color: '#DC2626',
                       cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem' }}>
              <XCircle size={15} /> Rejeter
            </button>
            <button onClick={() => onAction(item, 'VALIDE')}
              style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                       padding: '11px', borderRadius: 10, border: 'none',
                       background: 'linear-gradient(135deg, #059669, #047857)',
                       color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem',
                       boxShadow: '0 2px 8px rgba(5,150,105,0.35)' }}>
              <CheckCircle2 size={15} /> Valider
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Vue d'une catégorie ───────────────────────────────────────
function CategoryView({ catKey, onBack, counts }) {
  const cat = CATEGORIES.find(c => c.key === catKey);
  const Icon = cat.icon;

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [drawer,  setDrawer]  = useState(null);
  const [filterUser, setFilterUser] = useState('');
  const [showAll, setShowAll] = useState(false);

  const MONTHS = MONTHS_FR;

  const fetchItems = useCallback(() => {
    setLoading(true);
    const fn = {
      timesheets:     () => validationsAPI.listTimesheets(showAll ? { statut: undefined } : {}),
      notes_frais:    () => validationsAPI.listNotesFrais(showAll ? { statut: undefined } : {}),
      absences:       () => validationsAPI.listAbsences(showAll ? { statut: undefined } : {}),
      arrets_maladie: () => validationsAPI.listArrets(showAll ? { statut: undefined } : {}),
      documents:      () => validationsAPI.listDocuments(showAll ? { statut: undefined } : {}),
    }[catKey];
    fn().then(r => setItems(r.data || [])).catch(() => toast.error('Erreur')).finally(() => setLoading(false));
  }, [catKey, showAll]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = items.filter(it => {
    if (!filterUser) return true;
    const name = `${it.user_prenom || ''} ${it.user_nom || ''}`.toLowerCase();
    return name.includes(filterUser.toLowerCase());
  });

  const doAction = async (item, action, commentaire) => {
    try {
      if (catKey === 'timesheets') {
        if (action === 'VALIDE') await validationsAPI.validateTimesheet(item.id, commentaire ? { commentaire_rh: commentaire } : {});
        else await validationsAPI.rejectTimesheet(item.id, { commentaire_rh: commentaire });
      } else if (catKey === 'notes_frais') {
        if (action === 'VALIDE') await validationsAPI.validateNote(item.id, { commentaire });
        else await validationsAPI.rejectNote(item.id, { commentaire });
      } else if (catKey === 'absences') {
        if (action === 'VALIDE') await validationsAPI.validateAbsence(item.id, { commentaire_rh: commentaire });
        else await validationsAPI.rejectAbsence(item.id, { commentaire_rh: commentaire });
      } else if (catKey === 'arrets_maladie') {
        const payload = { commentaire_rh: commentaire || null };
        if (action === 'VALIDE') await validationsAPI.validateArret(item.id, payload);
        else await validationsAPI.rejectArret(item.id, payload);
      } else if (catKey === 'documents') {
        await validationsAPI.validateDocument(item.id, { action, commentaire });
      }
      toast.success(action === 'VALIDE' ? 'Validé ✓' : 'Rejeté');
      setModal(null);
      setDrawer(null);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const renderItem = (it) => {
    const statut = it.statut || 'SOUMIS';
    const isPending = statut === 'SOUMIS';
    const statutColor = statut === 'VALIDE' ? '#059669' : statut === 'REJETE' || statut === 'REFUSE' ? '#DC2626' : '#0891B2';

    let title = '';
    let sub   = '';
    if (catKey === 'timesheets') {
      title = `Feuille ${MONTHS[(it.mois || 1) - 1]} ${it.annee}`;
      sub   = `${it.user_prenom} ${it.user_nom}`;
    } else if (catKey === 'notes_frais') {
      title = it.designation || 'Note de frais';
      sub   = `${it.user_prenom} ${it.user_nom} · ${it.montant_ttc != null ? parseFloat(it.montant_ttc).toFixed(2) : '?'} €`;
    } else if (catKey === 'absences') {
      title = `Congé ${(it.type_conge || '').replace('_', ' ')} · ${it.nb_jours}j`;
      sub   = `${it.user_prenom} ${it.user_nom} · ${fmtDate(it.date_debut)} → ${fmtDate(it.date_fin)}`;
    } else if (catKey === 'arrets_maladie') {
      title = `Arrêt maladie · ${it.nb_jours}j`;
      sub   = `${it.user_prenom} ${it.user_nom} · ${fmtDate(it.date_debut)} → ${fmtDate(it.date_fin)}`;
    } else if (catKey === 'documents') {
      title = it.title || 'Document';
      sub   = `${it.user_prenom} ${it.user_nom} · ${(it.document_type || '').replace('_', ' ')}`;
    }

    return (
      <div key={it.id}
        onClick={() => setDrawer(it)}
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '14px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer', transition: 'border-color 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = cat.color + '60'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >
        <div style={{ width: 42, height: 42, borderRadius: 10, background: `${cat.color}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={20} color={cat.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)',
                           overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
            </span>
            <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3,
                           padding: '2px 8px', borderRadius: 20, fontSize: '0.67rem', fontWeight: 700,
                           background: `${statutColor}12`, color: statutColor, flexShrink: 0 }}>
              {statut}
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{sub}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Eye size={12} /> Voir
          </span>
          {isPending && (
            <>
              <button onClick={e => { e.stopPropagation(); setModal({ item: it, action: 'REJETE' }); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8,
                         border: '1px solid rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.07)',
                         color: '#DC2626', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                <XCircle size={13} /> Rejeter
              </button>
              <button onClick={e => { e.stopPropagation(); setModal({ item: it, action: 'VALIDE' }); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8,
                         border: 'none', background: '#059669', color: '#fff',
                         cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>
                <CheckCircle2 size={13} /> Valider
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack}
          style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
                   background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: `${cat.color}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={cat.color} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: 'var(--text-primary)' }}>
            {cat.label}
          </h2>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {counts[catKey] ?? 0} en attente — cliquer sur une ligne pour voir le détail
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={fetchItems}
            style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
                     background: 'var(--bg-elevated)', cursor: 'pointer', color: 'var(--text-muted)',
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={14} />
          </button>
          <button onClick={() => setShowAll(s => !s)}
            style={{ padding: '6px 14px', borderRadius: 8,
                     border: `1.5px solid ${showAll ? cat.color : 'var(--border)'}`,
                     background: showAll ? `${cat.color}12` : 'var(--bg-elevated)',
                     color: showAll ? cat.color : 'var(--text-muted)', cursor: 'pointer',
                     fontWeight: 600, fontSize: '0.78rem' }}>
            {showAll ? 'En attente seulement' : 'Voir tout'}
          </button>
        </div>
      </div>

      {/* Filtre */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <Filter size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input placeholder="Filtrer par nom d'utilisateur…" value={filterUser} onChange={e => setFilterUser(e.target.value)}
          style={{ width: '100%', maxWidth: 320, padding: '8px 12px 8px 32px', borderRadius: 8, boxSizing: 'border-box',
                   border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                   color: 'var(--text-primary)', fontSize: '0.8rem', outline: 'none' }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: 'var(--bg-card)',
                      border: '1px solid var(--border)', borderRadius: 12 }}>
          <CheckCircle2 size={32} style={{ color: '#059669', opacity: 0.4, margin: '0 auto 10px' }} />
          <div style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
            {filterUser ? 'Aucun résultat pour ce filtre' : 'Aucun élément en attente'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(it => renderItem(it))}
        </div>
      )}

      {/* Drawer détail */}
      {drawer && (
        <DetailDrawer
          item={drawer}
          catKey={catKey}
          onClose={() => setDrawer(null)}
          onAction={(item, action) => {
            setDrawer(null);
            setModal({ item, action });
          }}
        />
      )}

      {/* Modal commentaire */}
      {modal && (
        <CommentModal
          title={modal.action === 'VALIDE'
            ? `Valider — ${cat.label}`
            : `Rejeter — ${cat.label}`}
          action={modal.action}
          requireComment={modal.action === 'REJETE'}
          onConfirm={comment => doAction(modal.item, modal.action, comment)}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ── Tableau historique ─────────────────────────────────────────
const HISTORY_TYPES = [
  { value: '', label: 'Tous les types' },
  { value: 'timesheets',    label: 'Feuilles de temps' },
  { value: 'notes_frais',  label: 'Notes de frais' },
  { value: 'absences',     label: 'Congés' },
  { value: 'arrets_maladie', label: 'Arrêts maladie' },
  { value: 'documents',    label: 'Documents' },
];

function HistoryTable() {
  const [data,    setData]    = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const [drawer,  setDrawer]  = useState(null); // { row } pour le détail
  const [filters, setFilters] = useState({
    type_validation: '', statut: '',
    date_action_from: '', date_action_to: '',
    date_soumission_from: '', date_soumission_to: '',
    valideur_id: '',
  });

  const load = useCallback(() => {
    if (!open) return;
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    validationsAPI.history(params)
      .then(r => setData(r.data))
      .catch(() => toast.error('Erreur historique'))
      .finally(() => setLoading(false));
  }, [filters, open]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const resetFilters = () => setFilters({ type_validation:'', statut:'', date_action_from:'',
    date_action_to:'', date_soumission_from:'', date_soumission_to:'', valideur_id:'' });

  const sc = s => s === 'VALIDE' ? '#059669' : ['REJETE','REFUSE'].includes(s) ? '#DC2626' : '#6B7280';
  const sl = s => s === 'VALIDE' ? 'Validé' : ['REJETE','REFUSE'].includes(s) ? 'Rejeté' : s;

  // ── Export CSV (Excel) ──────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Type','Collaborateur','Objet','Date soumission','Date action','Statut','Traité par','Motif'];
    const rows = data.items.map(r => [
      r.type_label,
      `${r.user_prenom} ${r.user_nom}`,
      r.label,
      fmtDate(r.date_soumission),
      fmtDate(r.date_action),
      sl(r.statut),
      r.valideur_prenom ? `${r.valideur_prenom} ${r.valideur_nom}` : '',
      r.motif || '',
    ]);
    const csv = '﻿' + [headers, ...rows]
      .map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `historique_validations_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Export PDF (impression) ────────────────────────────────
  const exportPDF = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const rows = data.items.map(r => `
      <tr>
        <td>${r.type_label}</td>
        <td>${r.user_prenom} ${r.user_nom}</td>
        <td>${r.label}</td>
        <td>${fmtDate(r.date_soumission)}</td>
        <td>${fmtDate(r.date_action)}</td>
        <td style="color:${sc(r.statut)};font-weight:700">${sl(r.statut)}</td>
        <td>${r.valideur_prenom ? `${r.valideur_prenom} ${r.valideur_nom}` : '—'}</td>
        <td>${r.motif || '—'}</td>
      </tr>`).join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Historique validations</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; padding: 20px; }
        h2 { font-size: 14px; margin-bottom: 6px; }
        p  { color: #666; font-size: 10px; margin-bottom: 14px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f3f4f6; padding: 7px 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
        td { padding: 7px 8px; border-bottom: 1px solid #e5e7eb; }
        tr:nth-child(even) td { background: #fafafa; }
      </style></head><body>
      <h2>Historique des validations RH — WEBusiness</h2>
      <p>Exporté le ${new Date().toLocaleDateString('fr-FR')} · ${data.total} enregistrement(s)</p>
      <table>
        <thead><tr>
          <th>Type</th><th>Collaborateur</th><th>Objet</th>
          <th>Soumis le</th><th>Action le</th><th>Statut</th><th>Traité par</th><th>Motif</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=()=>{window.print();}<\/script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div style={{ marginTop: 36, border: '1px solid var(--border)', borderRadius: 14,
                  background: 'var(--bg-card)', overflow: 'hidden' }}>

      {/* Toggle header */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <button onClick={() => setOpen(o => !o)}
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                   padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer',
                   color: 'var(--text-primary)', textAlign: 'left' }}>
          <Clock size={18} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontWeight: 700, fontSize: '1rem' }}>Historique des validations</span>
          {data.total > 0 && (
            <span style={{ whiteSpace: 'nowrap', padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
                           background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
              {data.total}
            </span>
          )}
          {open ? <ChevronUp size={16} style={{ color: 'var(--text-muted)', marginLeft: 4 }} />
                : <ChevronDown size={16} style={{ color: 'var(--text-muted)', marginLeft: 4 }} />}
        </button>
        {open && data.items.length > 0 && (
          <div style={{ display: 'flex', gap: 8, paddingRight: 16 }}>
            <button onClick={exportCSV}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7,
                       border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                       cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600 }}>
              <FileText size={13} /> Excel
            </button>
            <button onClick={exportPDF}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7,
                       border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                       cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600 }}>
              <FileText size={13} /> PDF
            </button>
          </div>
        )}
      </div>

      {open && (
        <div>
          {/* Filtres */}
          <div style={{ padding: '14px 20px 14px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <select value={filters.type_validation} onChange={e => setF('type_validation', e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)',
                       background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer' }}>
              {HISTORY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>

            <select value={filters.statut} onChange={e => setF('statut', e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)',
                       background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer' }}>
              <option value="">Tous les statuts</option>
              <option value="VALIDE">Validé</option>
              <option value="REJETE">Rejeté / Refusé</option>
            </select>

            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Action du</span>
              <input type="date" value={filters.date_action_from} onChange={e => setF('date_action_from', e.target.value)}
                style={{ padding: '7px 8px', borderRadius: 8, border: '1px solid var(--border)',
                         background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>au</span>
              <input type="date" value={filters.date_action_to} onChange={e => setF('date_action_to', e.target.value)}
                style={{ padding: '7px 8px', borderRadius: 8, border: '1px solid var(--border)',
                         background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Soumis du</span>
              <input type="date" value={filters.date_soumission_from} onChange={e => setF('date_soumission_from', e.target.value)}
                style={{ padding: '7px 8px', borderRadius: 8, border: '1px solid var(--border)',
                         background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>au</span>
              <input type="date" value={filters.date_soumission_to} onChange={e => setF('date_soumission_to', e.target.value)}
                style={{ padding: '7px 8px', borderRadius: 8, border: '1px solid var(--border)',
                         background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
            </div>

            <button onClick={resetFilters}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)',
                       background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              Réinitialiser
            </button>
            <button onClick={load} title="Rafraîchir"
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                       background: 'var(--bg-elevated)', cursor: 'pointer', color: 'var(--text-muted)',
                       display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RefreshCw size={13} />
            </button>
          </div>

          {/* Tableau */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-muted)' }}>Chargement…</div>
          ) : data.items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-muted)' }}>
              Aucune validation trouvée pour ces critères
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)' }}>
                    {['Type','Collaborateur','Objet','Soumis le','Action le','Statut','Traité par','Motif',''].map((h,i) => (
                      <th key={i} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600,
                                           color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase',
                                           letterSpacing: '0.05em', whiteSpace: 'nowrap',
                                           borderBottom: '1px solid var(--border)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row, idx) => {
                    const cat  = CATEGORIES.find(c => c.key === row.type);
                    const Icon = cat?.icon || FileText;
                    const color = sc(row.statut);
                    return (
                      <tr key={row.id + idx}
                        style={{ borderBottom: '1px solid var(--border)',
                                 background: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)',
                                 transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)'}>
                        {/* Type */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Icon size={12} style={{ color: cat?.color || 'var(--text-muted)', flexShrink: 0 }} />
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{row.type_label}</span>
                          </div>
                        </td>
                        {/* Collaborateur */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {row.user_prenom} {row.user_nom}
                        </td>
                        {/* Objet */}
                        <td style={{ padding: '9px 12px', maxWidth: 180 }}>
                          <span title={row.label} style={{ display: 'block', overflow: 'hidden',
                                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                            color: 'var(--text-secondary)' }}>
                            {row.label}
                          </span>
                        </td>
                        {/* Soumis */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                          {fmtDate(row.date_soumission)}
                        </td>
                        {/* Action */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                          {fmtDate(row.date_action)}
                        </td>
                        {/* Statut */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                          <span style={{ whiteSpace: 'nowrap', display: 'inline-block', padding: '3px 9px', borderRadius: 20,
                                         fontSize: '0.67rem', fontWeight: 700,
                                         background: color + '18', color }}>
                            {sl(row.statut)}
                          </span>
                        </td>
                        {/* Traité par */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {row.valideur_prenom
                            ? <><span style={{ fontWeight: 600 }}>{row.valideur_prenom}</span> {row.valideur_nom}</>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                        {/* Motif */}
                        <td style={{ padding: '9px 12px', maxWidth: 160 }}>
                          {row.motif
                            ? <span title={row.motif} style={{ display: 'block', overflow: 'hidden',
                                                               textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                               color: 'var(--text-muted)', fontSize: '0.78rem',
                                                               fontStyle: 'italic' }}>
                                {row.motif}
                              </span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                        {/* Voir détail */}
                        <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => setDrawer(row)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                                     borderRadius: 7, border: '1px solid var(--border)',
                                     background: 'var(--bg-elevated)', cursor: 'pointer',
                                     color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 600 }}>
                            <Eye size={12} /> Voir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)',
                            fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                {data.items.length} / {data.total} enregistrement(s)
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drawer détail historique */}
      {drawer && (
        <DetailDrawer
          item={{ ...drawer, statut: drawer.statut }}
          catKey={drawer.type}
          onClose={() => setDrawer(null)}
          onAction={() => setDrawer(null)}
        />
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function RhValidations() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin', 'rh'].includes(user?.role?.toLowerCase());

  const [counts,  setCounts]  = useState({});
  const [loading, setLoading] = useState(true);
  const [active,  setActive]  = useState(null);
  const [sending, setSending] = useState(false);

  const now = new Date();

  const fetchCounts = () => {
    setLoading(true);
    validationsAPI.pendingCounts()
      .then(r => setCounts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCounts(); }, []);

  if (!isAdmin) {
    return (
      <div style={{ padding: '80px 40px', textAlign: 'center' }}>
        <AlertCircle size={36} style={{ color: 'var(--text-muted)', margin: '0 auto 12px' }} />
        <div style={{ color: 'var(--text-muted)' }}>Accès réservé aux administrateurs et RH</div>
      </div>
    );
  }

  const sendRappels = async () => {
    setSending(true);
    try {
      const res = await validationsAPI.sendRappels(now.getFullYear(), now.getMonth() + 1);
      toast.success(`${res.data.rappels_sent} rappel(s) envoyé(s)`);
    } catch { toast.error('Erreur'); }
    finally { setSending(false); }
  };

  if (active) {
    return (
      <div style={{ padding: '28px 40px' }}>
        <CategoryView catKey={active} onBack={() => { setActive(null); fetchCounts(); }} counts={counts} />
      </div>
    );
  }

  const total = counts.total || 0;

  return (
    <div style={{ padding: '28px 40px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
            Validations
          </h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Validez les feuilles de temps, notes de frais, congés, arrêts maladie et documents.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={fetchCounts}
            style={{ width: 36, height: 36, borderRadius: 9, border: '1px solid var(--border)',
                     background: 'var(--bg-elevated)', cursor: 'pointer', color: 'var(--text-muted)',
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={15} />
          </button>
          <button onClick={sendRappels} disabled={sending}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 9,
                     border: '1.5px solid var(--border)', background: 'var(--bg-card)',
                     color: 'var(--text-secondary)', cursor: sending ? 'wait' : 'pointer',
                     fontWeight: 600, fontSize: '0.8rem' }}>
            <Bell size={14} />
            {sending ? 'Envoi…' : `Rappels feuilles ${MONTHS_FR[now.getMonth()]}`}
          </button>
        </div>
      </div>

      {/* Badge total */}
      {!loading && total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderRadius: 11,
                      background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.22)', marginBottom: 24 }}>
          <AlertCircle size={17} style={{ color: '#3B82F6', flexShrink: 0 }} />
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {total} élément{total > 1 ? 's' : ''} en attente de validation
          </div>
        </div>
      )}

      {/* Cards catégories */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          {CATEGORIES.map(({ key, label, icon: Icon, color }) => {
            const count = counts[key] ?? 0;
            return (
              <div key={key} onClick={() => setActive(key)}
                style={{ background: 'var(--bg-card)', border: `1.5px solid ${count > 0 ? color + '44' : 'var(--border)'}`,
                         borderRadius: 14, padding: '20px 22px', cursor: 'pointer',
                         transition: 'all 0.15s', position: 'relative', overflow: 'hidden',
                         boxShadow: count > 0 ? `0 4px 20px ${color}18` : '0 1px 4px rgba(0,0,0,0.06)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 10px 28px ${color}22`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = count > 0 ? `0 4px 20px ${color}18` : '0 1px 4px rgba(0,0,0,0.06)'; }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: '14px 14px 0 0' }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, marginTop: 4 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 11, background: `${color}14`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={22} color={color} />
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 800, color: count > 0 ? color : 'var(--text-primary)', lineHeight: 1 }}>
                  {count}
                </div>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color, marginTop: 6 }}>{label}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3 }}>
                  {count === 0 ? 'Tout est à jour' : `${count} en attente`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Historique */}
      <HistoryTable />
    </div>
  );
}
